import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { ContextUserProfile } from '../../../contexts/UserProfileContext';
import api from '../../../services/api';
import endpoints from '../../../utils/commonVariables/endpoints';
import { viewModelEntities } from '../../../utils/commonVariables/entityKeys';
import useEncodedUrlId from '../../../customHooks/useEncodedUrlId';
import '../../../stylesheets/stylesLayout/privateDetail.scss';
import { NotificationsContext } from '../../../contexts/NotificationsContext';
import Tooltip from '../../../muiComponents/Tooltip';
import routes from '../../../utils/commonVariables/routes';
import IconButton from '../../../muiComponents/IconButton';
import supplierBookingViewModel from '../../../utils/viewModelStructures/SupplierBooking';
import {
    BookingState,
    CreditCardState,
} from '../../../types/booking/BookingTypes';
import {
    creditCardInitialState,
    detailBookingInitialState,
} from '../../../utils/bookings/bookingUtils';
import CopyOnHover from '../components/CopyOnHover';
import MainInformation from './bookingDetail/MainInformation';
import HotelInfo from './bookingDetail/HotelInfo';
import Finance from './bookingDetail/Finance';
import Notes from './bookingDetail/Notes';
import { adaptDDBBBookingToState } from '../../../services/booking/bookingAdapters';
import DotIcon from '../../components/DotIcon';
import BookingDetailLoading from './bookingDetail/BookingDetailLoading';

const BookingDetail = () => {
    const { translate, language } = useContext(ContextUserProfile);
    const { setErrorMessage } = useContext(NotificationsContext);

    const history = useHistory();
    const [booking, setBooking] = useState<BookingState>(
        detailBookingInitialState
    );
    const [creditCard, setCreditCard] = useState<CreditCardState>(
        creditCardInitialState
    );
    const [isFetching, setIsFetching] = useState(false);

    // console.log({ booking });
    const IdBooking = useEncodedUrlId('encodedIdBooking');
    const getBooking = async () => {
        setIsFetching(true);
        const endpoint = endpoints.viewModel(
            viewModelEntities.SupplierBooking,
            IdBooking
        );
        const {
            data: { Success, Result: _booking, Messages = [] },
        } = await api.post(endpoint, supplierBookingViewModel);
        if (Success && _booking) {
            const cleanBooking = adaptDDBBBookingToState(_booking);

            setBooking(cleanBooking);
            if (cleanBooking.paymentId > 0) {
                await getPaymentInfo(cleanBooking.paymentId);
                setCreditCard(prev => ({
                    ...prev,
                    creditCardHolderName:
                        _booking?.SupplierBookingPaymentInfo?.HolderName || '',
                    cvc:
                        _booking?.SupplierBookingPaymentInfo?.VirtualCreditCard
                            ?.CVV || '',
                }));
            }
        } else {
            let [{ Title: errorMessage }] = Messages;
            if (errorMessage.includes('auth')) {
                errorMessage = 'unauthorizedErrorMessage';
            }
            setErrorMessage(errorMessage);
        }
        setIsFetching(false);
    };

    useEffect(() => {
        if (IdBooking) {
            getBooking();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [IdBooking, language]);

    const getPaymentInfo = async (paymentId: number) => {
        const endpoint = endpoints.getCreditCardNumber(paymentId);
        const { data } = await api.get(endpoint);
        const { Result: _cardDetails, StatusCode } = data;
        if (StatusCode === 200) {
            setCreditCard(prev => ({
                ...prev,
                creditCardNumber: _cardDetails.CreditCardNumber
                    ? _cardDetails?.CreditCardNumber.replace(/-/g, '')
                    : '',
                creditCardExpiryDate: _cardDetails?.ExpirationDate || '',
            }));
        }
    };

    const closePage = () => {
        history.push(routes.landingPrivate + routes.booking);
    };

    return (
        <div className='booking-detail__container'>
            {isFetching ? (
                <BookingDetailLoading />
            ) : (
                <>
                    <div className='booking__title flex-between flex-align-center'>
                        <Tooltip titleText='booking'>
                            <span>
                                <CopyOnHover textToCopy={booking.bookingCode}>
                                    <h1>
                                        {translate('booking')}:{' '}
                                        {booking.bookingCode}
                                    </h1>
                                </CopyOnHover>
                            </span>
                        </Tooltip>
                        <div className='flex gap-20'>
                            <div className='flex flex-align-center gap-5'>
                                <DotIcon
                                    restStyles={{ color: booking.statusColor }}
                                />
                                <p>{booking.status}</p>
                            </div>
                            <IconButton iconName='Close' onClick={closePage} />
                        </div>
                    </div>
                    <main className='booking__main'>
                        <MainInformation
                            bookingCode={booking.bookingCode}
                            hotelRefecerenceCode={booking.hotelRefecerenceCode}
                            bookingDate={booking.bookingDate}
                            checkIn={booking.checkIn}
                            checkOut={booking.checkOut}
                            customerName={booking.customerName}
                            customerEmail={booking.customerEmail}
                            nights={booking.nights}
                            paxes={booking.paxes}
                        />
                        <HotelInfo
                            hotel={booking.hotel}
                            rating={booking.hotelRating}
                            hotelCategory={booking.hotelCategory}
                            address={booking.address}
                            rooms={booking.rooms}
                            hotelImages={booking.hotelImages}
                        />
                        <Finance
                            paymentId={booking.paymentId}
                            totalAmount={booking.totalAmount}
                            currency={booking.currency}
                            creditCard={creditCard}
                            cancellationPolicies={booking.cancellationPolicies}
                            prices={booking.prices}
                        />
                        {booking.notes ? <Notes notes={booking.notes} /> : ''}
                    </main>
                </>
            )}
        </div>
    );
};

export default BookingDetail;
